import { ReactElement, useState, useEffect, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  HiOutlineCheckCircle,
  HiOutlinePencilSquare,
  HiOutlinePlus,
  HiXMark,
} from 'react-icons/hi2';

import { FormProvider, useFieldArray, useForm, useFormState } from 'react-hook-form';
import { useCreateConfig, useUpdateConfig, useDeleteConfig, useBatchUpdateConfigs } from '../../../../hooks/useConfigs';
import { useUpdateRun } from '../../../../hooks/useRuns';
import { useReprocess } from '../../../../hooks/useReprocess';
import toast from 'react-hot-toast';
import Button from '../../../../components/Button';
import Spinner from '../../../../components/Spinner';
import SessionRunOrder from './SessionRunOrder';
import {
  useInvalidateSession,
  useUpdateSession,
  useReorderConfigs,
} from '../../../../hooks/useSessions';
import { useUnsavedChangesWarning } from '../../../../hooks/useUnsavedChangesWarning';
import { usePersistentViewState } from '../../../../hooks/usePersistentViewState';
import { v4 as uuidv4 } from 'uuid';
import { numberToLetters } from '../../../../utils/letterNumber';
import { DISC_LETTER_NUMBER } from '../../../../constants/apiConstants';
import ButtonList from 'components/ButtonList';
import { Session } from 'types/Session';
import SessionConfigsList from './SessionConfigsList';
import SessionEquipmentList from './SessionEquipmentList';
import SessionCalibrations from './SessionCalibrations';
import SessionStatusBar from './SessionStatusBar';

import { EditOperationMode, isOperationLocked } from './editOperationUtils';

export default function SessionConfigs(): ReactElement {
  const { session, isSessionFetching } = useOutletContext<{
    session: any;
    isSessionFetching: boolean;
  }>();

  const [isEdit, setIsEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Id of the config a new config is currently being inserted below — drives
  // the in-place "Adding config..." bar.
  const [addingBelowConfigId, setAddingBelowConfigId] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [viewType, setViewType] = usePersistentViewState({
    sessionId: session?.id || 'default',
    defaultView: 'config',
  });
  const [showAllRuns, setShowAllRuns] = useState(true);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [configMovesToSave, setConfigMovesToSave] = useState<{ [key: string]: () => Promise<void> }>({});
  const [changedConfigIds, setChangedConfigIds] = useState<Set<string>>(new Set());
  const [editOpMode, setEditOpMode] = useState<EditOperationMode>('idle');

  // Ref to store equipment save function
  const equipmentSaveRef = useRef<(() => Promise<void>) | null>(null);

  const invalidateSession = useInvalidateSession(session.id);

  // State management for config flags
  const formMethods = useForm<Session>({ defaultValues: session });
  const {
    replace: replaceConfigControls,
    fields: configControls,
  } = useFieldArray({
    control: formMethods.control,
    name: 'configs',
    keyName: 'fieldId',
  });

  useEffect(() => {
    formMethods.reset(session);
  }, [session]);

  const { dirtyFields } = useFormState({ control: formMethods.control });
  const updateConfig = useUpdateConfig();
  const batchUpdateConfigs = useBatchUpdateConfigs();
  const createConfig = useCreateConfig();
  const deleteConfig = useDeleteConfig();
  const updateRun = useUpdateRun();
  const updateSession = useUpdateSession(session.id);
  const reorderConfigsMutation = useReorderConfigs(session.id);

  // Use reprocess hook
  const { handleReprocess, removeAllReprocess } =
    useReprocess();

  const runOrder = session?.run_order || [];
  const [plannedRunConfigIds, setPlannedRunConfigIds] = useState(runOrder);

  const runs = configControls.flatMap((config: any) => config?.runs);
  const nextRunIndex = runs?.length || 0;

  // Use unsaved changes warning hook at SessionConfigs level
  const { clearUnsavedChanges, markUnsavedChange, clearUnsavedChange } = useUnsavedChangesWarning({
    enabled: isEdit,
    message: 'You have unsaved changes to this session configuration. Are you sure you want to leave?',
    formMethods: formMethods,
  });

  // Track changes to plannedRunConfigIds as manual changes
  useEffect(() => {
    const hasRunOrderChanged = JSON.stringify(plannedRunConfigIds) !== JSON.stringify(session.run_order);
    if (hasRunOrderChanged) {
      markUnsavedChange('runOrder');
    } else {
      clearUnsavedChange('runOrder');
    }
  }, [plannedRunConfigIds, session.run_order, markUnsavedChange, clearUnsavedChange]);


  const save = async () => {
    setIsSaving(true);
    const formValues = formMethods.getValues();
    const formConfigs = formValues.configs;

    // Check if we need to send new_order for reordering
    const isReorderOperation = changedConfigIds.size > 0;
    if (isReorderOperation) {
      await saveWithReorder(formConfigs);
    } else {
      await saveRunOrder();
    }

    // Save equipment changes if available
    if (equipmentSaveRef.current) {
      await equipmentSaveRef.current();
    }
    await saveDeletedConfigs(formConfigs);
    await saveNewConfigs(formConfigs);
    // Only save existing configs if we didn't just do a reorder operation
    // (reorder already updates configs via the new_order endpoint)
    if (!isReorderOperation) {
      await saveExistingConfigs(formConfigs);
    }
    await removeDeletedConfigControls(formConfigs);
    await saveConfigMovesToSave();
    // Asynchronously reprocess runs
    handleReprocess(invalidateSession);

    // Invalidate session to refresh data after deletions/updates
    invalidateSession();

    clearUnsavedChanges();
    setChangedConfigIds(new Set()); // Clear changed configs
    setEditOpMode('idle');

    setIsEdit(false);
    setIsSaving(false);
  };

  const saveWithReorder = async (formConfigs: any[]) => {
    try {
      // Create new_order array with config IDs in their current order
      const newOrder = formConfigs
        .filter((config) => config.letter_number !== DISC_LETTER_NUMBER && !config.isDeleted)
        .map((config) => config.id);

      const sessionUpdates: any = {
        testsession_id: session.id,
        new_order: newOrder,
      };

      // Check if run order has also changed
      if (JSON.stringify(plannedRunConfigIds) !== JSON.stringify(session.run_order)) {
        sessionUpdates.run_order = plannedRunConfigIds;
      }

      await reorderConfigsMutation.mutateAsync(sessionUpdates);
    } catch (e) {
      console.error(e);
      toast.error('Error reordering configurations');
      return;
    }
  };

  const checkSessionChanges = () => {
    const sessionUpdates: any = {};

    // Check if run order has changed
    if (JSON.stringify(plannedRunConfigIds) !== JSON.stringify(session.run_order)) {
      sessionUpdates.run_order = plannedRunConfigIds;
    }

    return sessionUpdates;
  };

  const saveRunOrder = async () => {
    // Use the centralized session change detection
    const sessionUpdates = checkSessionChanges();

    // Only make API call if there are updates
    if (Object.keys(sessionUpdates).length > 0) {
      try {
        await updateSession.mutateAsync({
          id: session.id,
          ...sessionUpdates,
        });
      } catch (e) {
        console.error(e);
        toast.error('Error updating session');
        return;
      }
    }
  };

  const saveNewConfigs = async (formConfigs: any[]) => {
    try {
      for (const [index, config] of formConfigs.entries()) {
        // POST (create)
        if (config.isNew && !config.isDeleted) {
          await createConfig.mutateAsync({ ...config, testsession_id: session.id });
          formMethods.setValue(`configs.${index}.isNew`, false);
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Unknown error creating config(s)');
      return;
    }
  };

  const saveExistingConfigs = async (formConfigs: any[]) => {
    try {
      if (dirtyFields.configs) {
        const configsToUpdate: any[] = [];

        // Helper function to validate and clean UUID fields
        const validateUUIDFields = (config: any) => {
          const uuidFields = [
            'baseline_id',
            'frame_id',
            'tyre_front_id',
            'tyre_rear_id',
            'wheel_front_id',
            'wheel_rear_id',
          ];

          uuidFields.forEach((field) => {
            if (!config[field]) {
              config[field] = null; // This will write "" to null
            } else if (config[field] && typeof config[field] === 'string' && config[field].length < 32) {
              config[field] = null;
            }
          });
        };

        // Collect all configs that need updates
        for (const index of Object.keys(dirtyFields.configs)) {
          const numericIndex = parseInt(index);
          const configValues = formConfigs[numericIndex];

          // Only update configs that have an id (i.e., not new/unsaved configs) and are not marked for deletion
          if (configValues && configValues.id && !configValues.isDeleted && !configValues.isNew) {
            // Find the original config from session to compare
            const originalConfig = session.configs.find((c: any) => c.id === configValues.id);

            // Check if the config has actually changed by comparing key fields
            const hasActualChanges =
              !originalConfig ||
              originalConfig.letter_number !== configValues.letter_number ||
              originalConfig.description !== configValues.description ||
              originalConfig.baseline_id !== configValues.baseline_id ||
              originalConfig.autocal_skip !== configValues.autocal_skip ||
              JSON.stringify(originalConfig.autocal_params) !== JSON.stringify(configValues.autocal_params) ||
              // Equipment fields
              originalConfig.frame_id !== configValues.frame_id ||
              originalConfig.tyre_front_id !== configValues.tyre_front_id ||
              originalConfig.tyre_rear_id !== configValues.tyre_rear_id ||
              originalConfig.wheel_front_id !== configValues.wheel_front_id ||
              originalConfig.wheel_rear_id !== configValues.wheel_rear_id ||
              originalConfig.tyre_front_pressure !== configValues.tyre_front_pressure ||
              originalConfig.tyre_rear_pressure !== configValues.tyre_rear_pressure ||
              JSON.stringify(originalConfig.dampers) !== JSON.stringify(configValues.dampers);

            if (hasActualChanges) {
              // Clean up fields that shouldn't be sent to the backend
              const cleanedConfigValues = { ...configValues };
              validateUUIDFields(cleanedConfigValues);

              configsToUpdate.push(cleanedConfigValues);
            }
          }
        }

        // Batch update all configs that have changes
        if (configsToUpdate.length > 0) {
          const updatedConfigs = await batchUpdateConfigs.mutateAsync(configsToUpdate);

          // Update form configs with the returned data if needed
          if (updatedConfigs) {
            for (const index of Object.keys(dirtyFields.configs)) {
              const numericIndex = parseInt(index);
              const configValues = formConfigs[numericIndex];

              if (configValues && configValues.id) {
                const updatedConfig = Array.isArray(updatedConfigs)
                  ? updatedConfigs.find((c: any) => c.id === configValues.id)
                  : updatedConfigs;
                if (updatedConfig) {
                  formConfigs[numericIndex] = { ...formConfigs[numericIndex], ...updatedConfig };
                }
              }
            }
          }
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Unknown error saving configs');
      return;
    }
  };

  const saveDeletedConfigs = async (formConfigs: any[]) => {
    // Delete configs marked for deletion
    try {
      for (const config of formConfigs) {
        if (config.isDeleted && config.id && !config.isNew) {
          await deleteConfig.mutateAsync(config.id);
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Error deleting config(s)');
      return;
    }
  };
  const removeDeletedConfigControls = (formConfigs: any[]) => {
    const remainingConfigs: any[] = [];
    for (const config of formConfigs) {
      if (!config.isDeleted) {
        remainingConfigs.push(config);
      }
    }

    // Remove deleted configs from form controls
    try {
      replaceConfigControls(remainingConfigs);
    } catch (e) {
      console.error(e);
      toast.error('Error removing config(s)');
      return;
    }
  };

  const saveConfigMovesToSave = async () => {
    for (const configId in configMovesToSave) {
      await configMovesToSave[configId]();
    }
  };

  const cancel = () => {
    // Add isDeleted field to all configs if it doesn't exist
    formMethods.reset(session);
    setPlannedRunConfigIds(session.run_order || []);
    setIsEdit(false);
    removeAllReprocess();
    setConfigMovesToSave({});
    setEditOpMode('idle');

    clearUnsavedChanges();
  };

  const defaultDescription = (letterNumber: number): string => {
    return `Config ${numberToLetters(letterNumber)}`;
  };

  // Adds a config immediately (create + reorder), not deferred to Save. The
  // create uses a collision-free append slot and the two-pass reorder endpoint
  // owns final letter_number assignment — see the unique-constraint note below.
  const insertNewConfig = async (insertAfterIndex?: number) => {
    if (isOperationLocked(editOpMode, 'create')) return;
    // An immediate insert refetches the session, which would discard pending
    // form edits — require the user to save those first.
    if (formMethods.formState.isDirty) {
      toast.error('Save your changes before adding a config');
      return;
    }
    setEditOpMode('create');
    setIsSaving(true);
    try {
      const currentConfigs = formMethods.getValues('configs') || [];

      // Ordered IDs of the non-DISC, non-deleted configs as they appear now,
      // plus the highest letter_number in use (for a collision-free slot).
      const orderedIds: string[] = [];
      let lastNonDiscArrayIndex = -1;
      let maxLetterNumber = -1;
      currentConfigs.forEach((config: any, i: number) => {
        if (!config || config.isDeleted || config.letter_number === DISC_LETTER_NUMBER) return;
        orderedIds.push(config.id);
        lastNonDiscArrayIndex = i;
        if (typeof config.letter_number === 'number' && config.letter_number > maxLetterNumber) {
          maxLetterNumber = config.letter_number;
        }
      });

      // The config we are inserting below — seeds the new config's baseline.
      const afterArrayIndex = insertAfterIndex ?? lastNonDiscArrayIndex;
      const aboveConfig = afterArrayIndex >= 0 ? currentConfigs[afterArrayIndex] : null;
      setAddingBelowConfigId(aboveConfig?.id ?? null);
      const orderPos = aboveConfig ? orderedIds.indexOf(aboveConfig.id) + 1 : orderedIds.length;

      const newConfigId = uuidv4();

      // description is unique per session — suggest a free name. The first
      // config of a session is "Baseline"; subsequent ones are "New Config N"
      // with N the lowest free integer >= 2.
      const usedDescriptions = new Set(
        currentConfigs.map((config: any) => config?.description).filter(Boolean),
      );
      let newDescription = `New Config ${newConfigId.slice(0, 8)}`;
      if (orderedIds.length === 0 && !usedDescriptions.has('Baseline')) {
        newDescription = 'Baseline';
      } else {
        for (let n = 2; n <= currentConfigs.length + 2; n += 1) {
          if (!usedDescriptions.has(`New Config ${n}`)) {
            newDescription = `New Config ${n}`;
            break;
          }
        }
      }

      // 1. Create at a free append slot (one past the current max) so the INSERT
      //    never collides with the unique letter_number / description constraints.
      await createConfig.mutateAsync({
        id: newConfigId,
        testsession_id: session.id,
        letter_number: maxLetterNumber + 1,
        description: newDescription,
        baseline_id: aboveConfig ? aboveConfig.baseline_id || aboveConfig.id : null,
      });

      // 2. Slot it into position via the two-pass reorder endpoint, which
      //    renumbers every config atomically without colliding.
      const newOrder = [...orderedIds];
      newOrder.splice(orderPos, 0, newConfigId);
      await reorderConfigsMutation.mutateAsync({
        testsession_id: session.id,
        new_order: newOrder,
      });

      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Failed to add configuration');
    } finally {
      setEditOpMode('idle');
      setIsSaving(false);
      setAddingBelowConfigId(null);
    }
  };

  const reorderConfigLetters = (): number => {
    // Assign sequential letters (A, B, C...) to non-deleted configs by array
    // position. Reads live form values via getValues so it stays correct even
    // immediately after a field-array insert, when `configControls` is stale.
    let currentLetterNumber = 0; // Start at 0 which corresponds to 'A'
    const configs = formMethods.getValues('configs') || [];

    configs.forEach((config: any, index: number) => {
      if (config?.isDeleted) {
        // Set deleted configs to undefined letter_number
        formMethods.setValue(`configs.${index}.letter_number`, undefined);
      } else if (config?.letter_number != DISC_LETTER_NUMBER) {
        // Assign sequential letter_number to non-deleted configs
        if (config.letter_number !== currentLetterNumber) {
          if (config.description === defaultDescription(config.letter_number)) {
            formMethods.setValue(`configs.${index}.description`, defaultDescription(currentLetterNumber));
          }
          formMethods.setValue(`configs.${index}.letter_number`, currentLetterNumber);
        }
        // Only increment letter_number for non-deleted configs
        currentLetterNumber += 1;
      } else {
        formMethods.setValue(`configs.${index}.letter_number`, DISC_LETTER_NUMBER);
      }
    });

    // Mark form as dirty
    formMethods.setValue('configs', formMethods.getValues('configs'), { shouldDirty: true });
    return currentLetterNumber;
  };

  const updateNextConfigId = (nextConfigId: string) => {
    // Find the first instance of nextConfigId and move it to the front
    setPlannedRunConfigIds((prev: string[]) => {
      const index = prev.findIndex((id: string) => id === nextConfigId);
      if (index === -1) return prev;
      const newArray = [...prev];
      const [movedItem] = newArray.splice(index, 1);
      return [movedItem, ...newArray];
    });
  };

  // Dynamic connector line between Plan tab and sub-tab buttons
  const connectorContainerRef = useRef<HTMLDivElement>(null);
  const subTabRef = useRef<HTMLDivElement>(null);
  const [connectorPath, setConnectorPath] = useState('');
  const [connectorSize, setConnectorSize] = useState({ w: 0, h: 0 });

  const updateConnector = useCallback(() => {
    const container = connectorContainerRef.current;
    const subTab = subTabRef.current;
    if (!container || !subTab) return;

    const planTab = document.querySelector(
      'nav[aria-label="Tabs"] a',
    ) as HTMLElement | null;
    if (!planTab) return;

    const containerRect = container.getBoundingClientRect();
    const planRect = planTab.getBoundingClientRect();
    const subTabRect = subTab.getBoundingClientRect();

    const allBtns = subTab.querySelectorAll('button');
    let activeBtn: Element | null = null;
    allBtns.forEach((btn) => {
      if (btn.className.includes('cr-bg-blue-900')) activeBtn = btn;
    });
    const subBtnRect = activeBtn
      ? activeBtn.getBoundingClientRect() : subTabRect;

    const startX = planRect.left + planRect.width / 2
      - containerRect.left;
    const startY = 0;
    const midY = (subBtnRect.top - containerRect.top) * 0.45;
    const endX = subBtnRect.left + subBtnRect.width / 2
      - containerRect.left;
    const endY = subBtnRect.top - containerRect.top;

    const svgW = Math.max(startX, endX) + 4;
    const svgH = endY + 2;

    setConnectorPath(
      `M ${startX} ${startY} L ${startX} ${midY}`
      + ` L ${endX} ${midY} L ${endX} ${endY}`,
    );
    setConnectorSize({ w: svgW, h: svgH });
  }, [viewType]);

  useEffect(() => {
    updateConnector();
    window.addEventListener('resize', updateConnector);
    return () => window.removeEventListener(
      'resize', updateConnector,
    );
  }, [updateConnector]);

  const buildToggleButtonClasses = (
    target: 'config' | 'run' | 'equipment',
  ) => {
    const baseClasses = [
      '!cr-w-32',
      '!cr-h-11',
      '!cr-border-0',
      '!cr-rounded-md',
      '!cr-px-3',
      '!cr-py-2',
      '!cr-text-base',
      '!cr-font-semibold',
      '!cr-transition-colors',
      '!cr-duration-200',
      '!cr-normal-case',
      '!cr-flex',
      '!cr-items-center',
      '!cr-justify-center',
      '!cr-whitespace-nowrap',
      '!cr-shadow-md',
      'font-forma',
      viewType === target
        ? '!cr-bg-blue-900 !cr-text-white'
        : '!cr-bg-cirrusBlue-100 !cr-text-blue-900 hover:!cr-bg-gray-350',
    ];
    return baseClasses.join(' ');
  };

  // Sub-tab buttons: Config List, Run List, Equip List
  const SubTabButtons = () => (
    <div className="cr-flex cr-flex-wrap cr-gap-2 md:cr-flex-nowrap">
      <Button
        onClick={() => {
          setViewType('config');
          setIsReordering(false);
        }}
        className={buildToggleButtonClasses('config')}
      >
        <span className="cr-font-main">Config List</span>
      </Button>
      <Button
        onClick={() => {
          setViewType('run');
          setIsReordering(true);
        }}
        className={buildToggleButtonClasses('run')}
      >
        <span className="cr-font-main">Run List</span>
      </Button>
      <Button
        onClick={() => {
          setViewType('equipment');
          setIsReordering(false);
        }}
        className={buildToggleButtonClasses('equipment')}
      >
        <span className="cr-font-main">Equip List</span>
      </Button>
    </div>
  );

  // Edit/Save/Cancel and New Config buttons
  const OptionsButtons = () => {
    const optionsContainerClasses = [
      'cr-flex',
      'cr-w-full',
      'cr-flex-col',
      'cr-gap-2',
      'md:cr-flex-row',
      'md:cr-items-center',
      'md:cr-justify-between',
      'md:cr-gap-0',
    ].join(' ');

    return (
      <div className={optionsContainerClasses}>
        <ButtonList className="cr-flex-wrap !cr-gap-2 md:cr-flex-nowrap">
          {!isEdit && (
            <Button
              onClick={() => setIsEdit(true)}
              icon={<HiOutlinePencilSquare className={'cr-size-7'} />}
              variant="secondary"
            >
              Edit
            </Button>
          )}
          {isEdit && (
            <>
              <Button
                onClick={formMethods.handleSubmit(save)}
                icon={<HiOutlineCheckCircle className={'cr-size-7'} />}
                variant="secondary"
                className={editOpMode !== 'idle'
                  ? '!cr-bg-green-500 !cr-text-white !cr-border-green-500'
                  : ''}
              >
                Save
              </Button>
              <Button onClick={cancel} icon={<HiXMark className={'cr-size-7'} />} variant="secondary">
                Cancel
              </Button>
            </>
          )}
        </ButtonList>
        <ButtonList className="cr-w-full cr-flex-wrap cr-justify-start !cr-gap-2 md:cr-w-auto md:cr-flex-nowrap md:cr-justify-end">
          {isEdit && !isReordering && (
            <Button
              onClick={() => insertNewConfig()}
              icon={<HiOutlinePlus className={'cr-size-5'} />}
              variant="secondary"
              disabled={isOperationLocked(editOpMode, 'create')}
              className="cr-whitespace-nowrap"
            >
              New Config
            </Button>
          )}
        </ButtonList>
      </div>
    );
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form>
          <fieldset
            disabled={
              isSessionFetching || updateConfig.isPending || updateConfig.isPending || updateRun.isPending || isSaving
            }
          >
            {/* Sub-tab row with staggered connector from Plan tab */}
            <div
              className="cr-relative cr-pb-4 cr-font-main cr-text-base cr--mt-0.5"
              ref={connectorContainerRef}
            >
              {connectorPath && (
                <svg
                  className="cr-absolute cr-top-0 cr-left-0 cr-pointer-events-none cr-text-blue-900"
                  width={connectorSize.w}
                  height={connectorSize.h}
                  fill="none"
                >
                  <path
                    d={connectorPath}
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
              )}
              <div className="cr-ml-16 cr-pt-8" ref={subTabRef}>
                <SubTabButtons />
              </div>
            </div>
            {viewType !== 'config' && (
              <div className="cr-flex cr-flex-row cr-gap-4 cr-py-4 cr-font-main cr-text-base cr-text-lg">
                <OptionsButtons />
              </div>
            )}
            {viewType === 'config' && (
              <>
                <SessionStatusBar session={session} />
                <SessionConfigsList
                  session={session}
                  isEdit={isEdit}
                  configControls={configControls}
                  showAllRuns={showAllRuns}
                  setShowAllRuns={setShowAllRuns}
                  updateNextConfigId={updateNextConfigId}
                  plannedRunConfigIds={plannedRunConfigIds}
                  setPlannedRunConfigIds={setPlannedRunConfigIds}
                  nextRunIndex={nextRunIndex}
                  activeRunId={activeRunId}
                  setActiveRunId={setActiveRunId}
                  invalidateSession={invalidateSession}
                  reorderConfigLetters={reorderConfigLetters}
                  configMovesToSave={configMovesToSave}
                  changedConfigIds={changedConfigIds}
                  editOpMode={editOpMode}
                  setEditOpMode={setEditOpMode}
                  setChangedConfigIds={setChangedConfigIds}
                  onReprocessRun={() => setIsEdit(false)}
                  onEditConfig={() => setIsEdit(true)}
                  onSaveConfig={formMethods.handleSubmit(save)}
                  onCancelConfig={cancel}
                  onInsertConfigBelow={insertNewConfig}
                  addingBelowConfigId={addingBelowConfigId}
                />
              </>
            )}
            {viewType === 'run' && (
              <SessionRunOrder
                key="run-orders"
                configs={configControls}
                plannedRunConfigIds={plannedRunConfigIds}
                setPlannedRunConfigIds={setPlannedRunConfigIds}
                isEdit={isEdit}
              />
            )}
            {viewType === 'equipment' && isEdit && (
              <SessionEquipmentList
                session={session}
                configControls={configControls}
                markUnsavedChange={markUnsavedChange}
                clearUnsavedChange={clearUnsavedChange}
                equipmentSaveRef={equipmentSaveRef}
              />
            )}
            {viewType === 'equipment' && !isEdit && (
              <SessionEquipmentList
                session={session}
                configControls={configControls}
                readOnly={true}
                markUnsavedChange={markUnsavedChange}
                clearUnsavedChange={clearUnsavedChange}
                equipmentSaveRef={equipmentSaveRef}
              />
            )}
            <div className="cr-flex cr-flex-row cr-gap-4 cr-py-4 cr-font-main cr-text-base cr-text-lg">
              {viewType !== 'config' && <OptionsButtons />}
              <Spinner
                isFetching={
                  isSessionFetching || updateConfig.isPending || updateConfig.isPending || updateRun.isPending
                }
              />
            </div>
            <div className="cr-h-10" />
          </fieldset>
        </form>
        {/* Display calibration information from configs where branch == true */}
      </FormProvider>
      {session && session.configs && session.configs.length > 0 && <SessionCalibrations session={session} />}
    </>
  );
}
